import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { fechaDeCertificado } from './generarCertificado';

/**
 * El certificado **en pantalla** — la misma lámina que se descarga, dibujada en DOM.
 *
 * Es una transcripción del artboard "Certificado Atlas" de Claude Design
 * (ADR-2026-08-25-la-vista-en-pantalla-es-el-artboard-en-dom): mismos ornamentos
 * —el arte son los mismos SVG que usó el diseño—, mismas medidas, mismas
 * tipografías (Plus Jakarta Sans y Playfair Display ya viven en la app).
 *
 * **Por qué no se muestra el PDF directamente.** Sería la copia exacta y sin
 * duplicar nada, pero un PDF embebido en la página no se ve igual en todos lados:
 * Safari en iOS no lo dibuja dentro de un `iframe`, y donde sí se dibuja aparece con
 * el visor del navegador encima —barras, zoom, botones— que es cualquier cosa menos
 * "tu certificado". En DOM se ve nítido en cualquier pantalla, escala con el ancho
 * disponible y no depende de ningún visor.
 *
 * **La contra, asumida:** el arte vive en dos lados —este componente y el PDF de
 * fondo— y los dos salen del mismo artboard. Si el diseño cambia hay que rehacer los
 * dos; el proceso está escrito en
 * `docs/2026-08-25-certificado-de-claude-design-proceso.md`.
 */

/** El artboard mide esto. Todo lo de adentro está en sus píxeles. */
const ANCHO = 1056;
const ALTO = 816;

const TINTA = '#020B2E';
const SANS = '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif';
const SERIF = '"Playfair Display", Georgia, serif';

export interface Props {
  nombre: string;
  curso: string;
  /** Cuándo terminó el curso, en ms de época. */
  fecha: number;
}

/**
 * Achica el cuerpo de un texto hasta que entre en una línea.
 *
 * Es el equivalente en DOM de `ajustarAlAncho` del generador del PDF: el diseño se
 * calculó con un nombre típico y siempre aparece alguien con dos apellidos. Que las
 * dos vistas achiquen igual es lo que hace que lo que se ve en pantalla sea lo que
 * se descarga.
 */
function useAjusteDeTexto(texto: string, base: number, minimo: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [tamano, setTamano] = useState(base);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let actual = base;
    el.style.fontSize = `${actual}px`;
    // `scrollWidth > clientWidth` con `nowrap` es exactamente "no entra".
    while (actual > minimo && el.scrollWidth > el.clientWidth) {
      actual -= 0.5;
      el.style.fontSize = `${actual}px`;
    }
    setTamano(actual);
  }, [texto, base, minimo]);

  return { ref, tamano };
}

export function VistaCertificado({ nombre, curso, fecha }: Props) {
  const marco = useRef<HTMLDivElement>(null);
  const [escala, setEscala] = useState(1);

  /* La lámina se dibuja SIEMPRE a 1056 × 816 y se escala entera al ancho disponible.
     Es lo que mantiene las proporciones exactas del diseño en cualquier pantalla: si
     cada medida se hiciera relativa, el certificado dejaría de ser el certificado y
     pasaría a ser un layout responsive parecido. */
  useEffect(() => {
    const el = marco.current;
    if (!el) return;
    const medir = () => setEscala(el.clientWidth / ANCHO);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  const ajusteNombre = useAjusteDeTexto(nombre, 54, 30);
  const ajusteCurso = useAjusteDeTexto(curso, 32, 17);

  const cenefaH: CSSProperties = {
    position: 'absolute',
    left: 50,
    right: 50,
    height: 30,
    clipPath: 'polygon(0 0,100% 0,calc(100% - 30px) 100%,30px 100%)',
    backgroundImage: 'url(/certificados/cenefa-h.svg)',
    backgroundRepeat: 'round no-repeat',
    backgroundSize: '15px 30px',
  };
  const cenefaV: CSSProperties = {
    position: 'absolute',
    top: 50,
    bottom: 50,
    width: 30,
    clipPath: 'polygon(0 0,100% 30px,100% calc(100% - 30px),0 100%)',
    backgroundImage: 'url(/certificados/cenefa-v.svg)',
    backgroundRepeat: 'no-repeat round',
    backgroundSize: '30px 15px',
  };
  const linea: CSSProperties = {
    width: '100%',
    height: 1.5,
    background: 'rgba(2,11,46,0.35)',
  };
  const piePie: CSSProperties = { fontSize: 15, fontWeight: 700, marginTop: 8 };

  return (
    /* `aspect-ratio` da el alto —y lo reserva antes de que mida el observador, así la
       pantalla no pega un salto al montar—, y la lámina va ABSOLUTA adentro.

       Lo de absoluta no es decorativo: `transform: scale()` encoge lo que se VE pero no
       lo que la lámina OCUPA, así que en flujo normal seguía midiendo 816 px de alto e
       inflaba a este contenedor por encima de su proporción. El resultado era una
       franja en blanco de ~160 px debajo del certificado
       (ADR-2026-08-25-la-lamina-escalada-va-fuera-de-flujo). Fuera de flujo no puede
       empujar nada, y el alto queda exactamente el de la proporción. */
    <div
      ref={marco}
      style={{
        width: '100%',
        aspectRatio: `${ANCHO} / ${ALTO}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-label={`Certificado de finalización de ${curso} a nombre de ${nombre}`}
        style={{
          width: ANCHO,
          height: ALTO,
          transform: `scale(${escala})`,
          transformOrigin: 'top left',
          position: 'absolute',
          top: 0,
          left: 0,
          background: '#ffffff',
          color: TINTA,
          fontFamily: SANS,
          overflow: 'hidden',
        }}
      >
        {/* Marco azul con el papel adentro. */}
        <div style={{ position: 'absolute', inset: 28, padding: 12, background: '#01269b' }}>
          <div style={{ width: '100%', height: '100%', background: '#FDFCF9' }} />
        </div>

        {/* Fondo de ondas + el filete fino. */}
        <div
          style={{
            position: 'absolute',
            inset: 89,
            backgroundColor: '#FDFCF9',
            backgroundImage: 'url(/certificados/fondo-ondas.svg)',
            backgroundRepeat: 'repeat',
            backgroundSize: '240px 22px',
          }}
        />
        <div style={{ position: 'absolute', inset: 88, border: '1px solid rgba(22,68,235,0.22)' }} />

        {/* Las cuatro cenefas. Las de abajo y la derecha son las mismas espejadas. */}
        <div style={{ ...cenefaH, top: 50 }} />
        <div style={{ ...cenefaH, bottom: 50, transform: 'scaleY(-1)' }} />
        <div style={{ ...cenefaV, left: 50 }} />
        <div style={{ ...cenefaV, right: 50, transform: 'scaleX(-1)' }} />

        {/* Contenido. */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            padding: '120px 142px 112px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 32,
            }}
          >
            <img
              src="/certificados/logo-lockup-blue.svg"
              alt="Estudio Atlas"
              style={{ height: 26, width: 'auto', display: 'block', marginTop: 6, marginLeft: -23 }}
            />
            <img
              src="/certificados/sello.svg"
              alt=""
              style={{ height: 70, width: 'auto', display: 'block', marginTop: -18, marginRight: -28 }}
            />
          </div>

          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              paddingBottom: 8,
              minWidth: 0,
            }}
          >
            <div style={{ fontSize: 76, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1 }}>
              Certificado
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, marginTop: 10 }}>de Finalización</div>

            {/* EL NOMBRE. */}
            <div
              ref={ajusteNombre.ref}
              style={{
                fontSize: ajusteNombre.tamano,
                fontWeight: 800,
                color: '#1644eb',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                marginTop: 58,
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {nombre}
            </div>

            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: 'rgba(2,11,46,0.45)',
                marginTop: 64,
              }}
            >
              Completó con éxito
            </div>

            {/* EL CURSO. */}
            <div
              ref={ajusteCurso.ref}
              style={{
                fontSize: ajusteCurso.tamano,
                fontWeight: 700,
                letterSpacing: '-0.015em',
                marginTop: 10,
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {curso}
            </div>
          </div>

          {/* Los dos pies: la fecha y la firma. */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              gap: 110,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 220,
              }}
            >
              <div style={{ fontSize: 19, fontWeight: 700, paddingBottom: 8 }}>
                {fechaDeCertificado(fecha)}
              </div>
              <div style={linea} />
              <div style={piePie}>Fecha de emisión</div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 220,
              }}
            >
              <div
                style={{
                  fontFamily: SERIF,
                  fontStyle: 'italic',
                  fontWeight: 400,
                  fontSize: 25,
                  color: TINTA,
                  paddingBottom: 5,
                }}
              >
                Nacho Porras
              </div>
              <div style={linea} />
              <div style={piePie}>Fundador y CEO</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
